/**
 * The email-link sign-in decisions, kept free of the DOM so they can be
 * tested (AFHR-030 follow-up, 2026-09-17).
 *
 * Two screens share the one login card:
 *   - "password"    — the everyday form, plus "No password? Email me a
 *                     sign-in link" on tenant builds.
 *   - "finish-link" — someone has just opened a sign-in link. They have no
 *                     password, so the password box, "Forgot your password?"
 *                     and the send-a-link button are all noise; the only
 *                     thing left to do is confirm their email address.
 *
 * ☠️ Before this, the finish step reused the SEND button with a second click
 * listener added on top. The send listener was never removed, so pressing
 * "Finish signing in" also emailed a brand-new link — the duplicate email an
 * invitee received on 2026-09-17. One button, one job: the finish step now
 * submits the login form instead.
 */

export type LoginScreenMode = "password" | "link-first" | "finish-link";

/**
 * Which door to put in front of someone.
 *
 * ☠️ ON A TENANT BUILD THE PASSWORD BOX CANNOT WORK, and until 2026-09-21 it
 * was the primary control anyway. `training_invites.py` creates every
 * invitee with NO password ("NO password, email-link only", line 280) and
 * there is no code path anywhere that sets one. The only humans in
 * training/demo/develop with a password are the synthetic personas, whose
 * known password is deliberate fiction-only (seed_personas.py).
 *
 * So a real invitee — a member of staff, an external consultant — arrived at
 * a form demanding a credential they do not have and cannot obtain, with the
 * one door that works rendered as a de-emphasised link BELOW "Forgot your
 * password?" (which is itself a dead end there: no password exists to
 * reset). Nobody chose that. Production is password-only, the shared login
 * card was built for it, and the tenant case was added later behind a
 * `hidden` attribute without revisiting the ordering.
 *
 * `emailLinkEnabled` is the honest signal for this, and it is already gated
 * on `auth.tenantId`: true exactly where a working email-link door exists,
 * false on production where the main pool has no tenant and
 * sendSignInLinkToEmail cannot be offered at all. ADR-0045 §4 states the
 * intent — "tenants enable email-link sign-in as the DEFAULT door" — which
 * this finally makes true in the UI.
 */
export function loginScreenMode(emailLinkEnabled: boolean, isSignInLink: boolean): LoginScreenMode {
  if (!emailLinkEnabled) return "password";
  return isSignInLink ? "finish-link" : "link-first";
}

/** Primary button per screen. */
export function primaryActionLabel(mode: LoginScreenMode): string {
  switch (mode) {
    case "finish-link":
      return "Finish signing in";
    case "link-first":
      return "Email me a sign-in link";
    default:
      return "Sign In";
  }
}

/**
 * ☠️ The password escape hatch stays REACHABLE on tenant builds rather than
 * being removed. The personas do have passwords, and a demo is exactly the
 * moment someone needs to sign in without waiting on an inbox.
 */
export const USE_PASSWORD_INSTEAD = "Use a password instead";
export const USE_LINK_INSTEAD = "Email me a sign-in link instead";

/**
 * When the auth observer reports "signed out", should the login card fall
 * back to the password screen?
 *
 * ☠️ ONLY FROM THE FINISH-LINK SCREEN, AND ONLY ONCE THE LINK IS SPENT.
 * This exists for one case: a link sign-in that the environment gate then
 * refused, which lands signed-out with the link already consumed and the
 * finish screen still showing. Anywhere else it is wrong.
 *
 * Both halves are load-bearing, and each has now broken once:
 *
 * 1. Without the MODE half it fires on every screen. It used to read
 *    `pendingLink === null && passwordInput.hidden` — a proxy that was
 *    equivalent only while the finish screen was the one thing hiding the
 *    password box. The link-first default made it true on EVERY page load
 *    (Firebase fires the observer with a null user on init), so the new
 *    default was overwritten milliseconds after being set.
 *
 * 2. Without the PENDING-LINK half it fires on ARRIVAL. Someone opening a
 *    sign-in link is BY DEFINITION still signed out, so the observer fires
 *    null while the finish screen is up and the link is UNSPENT. A guard
 *    asking only about `mode` tears that screen down before they can
 *    confirm their email — and `showPasswordScreen` strips the oobCode
 *    from the URL on its way out, so the link cannot even be retried.
 *    Fixing (1) by replacing the WHOLE condition dropped this half, which
 *    had been doing real work all along. That is the 2026-09-21
 *    regression; the two-argument signature exists so it cannot recur.
 */
export function restoresPasswordScreenOnSignOut(
  mode: LoginScreenMode,
  hasPendingLink: boolean,
): boolean {
  return mode === "finish-link" && !hasPendingLink;
}

export const LINK_FIRST_PROMPT =
  "Enter your email and we'll send you a sign-in link — no password needed.";

export const FINISH_LINK_PROMPT =
  "You're almost signed in. Enter the email address this link was sent to, then press Finish signing in.";

export const LINK_SENT =
  "Check your inbox. The link signs you in on this device.";

/**
 * What a failed sign-in-link attempt means for the person holding it.
 *
 * `keepLink` false means the link itself is spent (used, expired, or
 * mangled): leave the finish screen and let them request a fresh one, rather
 * than inviting them to retype an email against a link that can never work.
 */
export interface LinkFailure {
  message: string;
  keepLink: boolean;
}

export function linkFailure(code: string): LinkFailure {
  switch (code) {
    case "auth/invalid-email":
      return {
        message: "That email doesn't match this sign-in link. Enter the address the link was sent to.",
        keepLink: true,
      };
    case "auth/invalid-action-code":
      return {
        message: "This sign-in link has already been used or isn't valid. Enter your email and request a new one.",
        keepLink: false,
      };
    case "auth/expired-action-code":
      return {
        message: "This sign-in link has expired. Enter your email and request a new one.",
        keepLink: false,
      };
    case "auth/user-disabled":
      return { message: "This account has been disabled. Contact the person who invited you.", keepLink: false };
    case "auth/unauthorized-continue-uri":
      return { message: "This site isn't authorised for sign-in links yet. Tell the office.", keepLink: false };
    case "auth/network-request-failed":
      return { message: "Couldn't reach the sign-in service. Check your connection and try again.", keepLink: true };
    default:
      return { message: code ? `Sign-in link failed (${code}).` : "Sign-in link failed.", keepLink: true };
  }
}

export function errorCode(err: unknown): string {
  return typeof err === "object" && err !== null && "code" in err
    ? String((err as { code: unknown }).code)
    : "";
}
